/**
 * Interface defining the size configuration for dashboard widgets.
 * Allows widgets to span multiple columns and rows in the grid layout.
 */
export interface IWidgetSize {
  /** Number of columns the widget should span (default: 1) */
  spanColumns?: number;
  /** Number of rows the widget should span (default: 1) */
  spanRows?: number;
}

const SCROLLBAR_MARGIN = 32;

/**
 * Calculates the optimal grid layout for dashboard widgets with support for spanning widgets.
 * Uses a two-phase placement algorithm: first places large widgets, then fills remaining space with single-cell widgets.
 * Automatically handles responsive behavior and mobile optimization.
 *
 * @param width - Available container width in pixels
 * @param height - Available container height in pixels
 * @param childCount - Number of widgets to place in the grid
 * @param widgetSizes - Array of size configurations for each widget (undefined for mobile)
 * @param minItemWidth - Minimum width for each grid item
 * @param maxItemWidth - Maximum width for each grid item
 * @param minItemHeight - Minimum height for each grid item
 * @param maxItemHeight - Maximum height for each grid item
 * @param gap - Space between grid items in pixels
 * @param forceSmallScreen - Force single-column mobile layout regardless of screen size
 * @returns Grid layout configuration with positions and dimensions
 */
export const calculateGridLayout = (
  width: number,
  height: number,
  childCount: number,
  widgetSizes: IWidgetSize[] | undefined,
  minItemWidth: number,
  maxItemWidth: number,
  minItemHeight: number,
  maxItemHeight: number,
  gap: number,
  forceSmallScreen: boolean
) => {
  if (childCount === 0) {
    return {
      columns: 1,
      itemWidth: width,
      itemHeight: height,
      totalHeight: 0,
      totalWidth: 0,
      grid: [],
    };
  }

  // Check if this is an extra small screen (typically mobile)
  const isExtraSmall = width < minItemWidth * 2 + gap || forceSmallScreen;
  const safeWidgetSizes = isExtraSmall ? undefined : widgetSizes;

  // Calculate grid size
  // Max number of columns based on min width + gaps
  const maxPossibleColumns =
    width >= minItemWidth + gap
      ? Math.floor((width + gap) / (minItemWidth + gap))
      : 1;
  const minNeededCells = Math.max(
    1,
    childCount +
      (safeWidgetSizes
        ? safeWidgetSizes.reduce(
            (acc, size) =>
              acc + (size.spanColumns ?? 1) * (size.spanRows ?? 1) - 1,
            0
          )
        : 0)
  );
  // Find highest row span
  const minNeededRows = safeWidgetSizes
    ? Math.max(1, ...safeWidgetSizes.map(size => size.spanRows ?? 1))
    : 1;
  let rowCount = Math.max(
    minNeededRows,
    Math.ceil(minNeededCells / maxPossibleColumns)
  );
  let colCount = Math.max(1, Math.ceil(minNeededCells / rowCount));
  let calculating = true;

  // Create grid placement algorithm for spanning items
  const grid: (number | null)[][] = [];
  const itemPositions: Array<{
    row: number;
    col: number;
    spanCols: number;
    spanRows: number;
  } | null> = new Array(childCount).fill(null);
  let counter = 0;
  while (calculating && counter < 10) {
    counter++;
    calculating = false;

    // Clear grid
    grid.length = 0;
    Array.from({ length: rowCount }).forEach(() =>
      grid.push(new Array(colCount).fill(null))
    );
    itemPositions.forEach((_, i) => (itemPositions[i] = null));

    // Try to place each large widget first
    let x = 0;
    let y = 0;
    let index = 0;
    while (index < childCount && !calculating) {
      const size = safeWidgetSizes?.[index];
      const spanCols = size?.spanColumns ?? 1;
      const spanRows = size?.spanRows ?? 1;
      if (spanCols > 1 || spanRows > 1) {
        const position = findAvailablePosition(grid, x, y, spanCols, spanRows);
        if (position) {
          markAreaOccupied(
            grid,
            position.row,
            position.col,
            spanRows,
            spanCols,
            index
          );
          itemPositions[index] = {
            row: position.row,
            col: position.col,
            spanCols,
            spanRows,
          };
          x = position.col + spanCols;
          if (x >= colCount) {
            x = 0;
            y++;
          }
        } else {
          // Could not place, need to increase grid size
          if (colCount < maxPossibleColumns) {
            colCount++;
          } else {
            rowCount++;
          }
          calculating = true;
        }
      } else {
        // Skip single cell widgets for now
        x++;
        if (x >= colCount) {
          x = 0;
          y++;
        }
      }
      index++;
    }

    // Place remaining widgets
    if (!calculating) {
      index = 0;
      while (index < childCount) {
        const size = safeWidgetSizes?.[index];
        const spanCols = size?.spanColumns ?? 1;
        const spanRows = size?.spanRows ?? 1;
        if (spanCols === 1 && spanRows === 1) {
          const position = findAvailablePosition(grid, 0, 0, 1, 1);
          if (position) {
            markAreaOccupied(grid, position.row, position.col, 1, 1, index);
            itemPositions[index] = {
              row: position.row,
              col: position.col,
              spanCols: 1,
              spanRows: 1,
            };
          }
        }
        index++;
      }
    }
  }

  // Remove unused columns
  for (let c = colCount - 1; c >= 1; c--) {
    let isEmpty = true;
    for (let r = 0; r < rowCount; r++) {
      if (grid[r] && grid[r][c] !== null) {
        isEmpty = false;
        break;
      }
    }
    if (isEmpty) {
      colCount--;
      break;
    }
  }

  // Remove unused rows
  for (let r = rowCount - 1; r >= 1; r--) {
    let isEmpty = true;
    for (let c = 0; c < colCount; c++) {
      if (grid[r] && grid[r][c] !== null) {
        isEmpty = false;
        break;
      }
    }
    if (isEmpty) {
      rowCount--;
      break;
    }
  }

  // Calculate itemWidth,  itemHeight, totalWidth and totalHeight
  const itemHeight = Math.min(
    Math.max(
      Math.floor((height - gap * (rowCount - 1)) / rowCount),
      minItemHeight
    ),
    maxItemHeight
  );
  const totalHeight = rowCount * itemHeight + (rowCount - 1) * gap;

  const itemWidth = Math.min(
    Math.max(
      Math.floor(
        (width -
          (totalHeight > height ? SCROLLBAR_MARGIN : 0) -
          gap * (colCount - 1)) /
          colCount
      ),
      minItemWidth
    ),
    maxItemWidth
  );
  const totalWidth = colCount * itemWidth + (colCount - 1) * gap;

  return {
    colCount,
    itemWidth,
    itemHeight,
    totalWidth,
    totalHeight,
    grid: itemPositions,
  };
};

/**
 * Finds the next available position in the grid where a widget of the specified size can be placed.
 * Searches forward from the given starting position, then backward if no space is found.
 *
 * @param grid - 2D array representing the current grid state (null = empty, number = widget index)
 * @param fromCol - Starting column to search from
 * @param fromRow - Starting row to search from
 * @param spanCols - Number of columns the widget needs
 * @param spanRows - Number of rows the widget needs
 * @returns Position object with row and column, or undefined if no space available
 */
const findAvailablePosition = (
  grid: (number | null)[][],
  fromCol: number,
  fromRow: number,
  spanCols: number,
  spanRows: number
): { row: number; col: number } | undefined => {
  // Search forward from (fromRow, fromCol)
  for (let row = fromRow; row <= grid.length - spanRows; row++) {
    for (
      let col = row === fromRow ? fromCol : 0;
      col <= grid[row].length - spanCols;
      col++
    ) {
      // Check if the area is free
      if (checkAreaFree(grid, row, col, spanRows, spanCols)) {
        return { row, col };
      }
    }
  }
  // If not found, search backward from (fromRow, fromCol)
  for (let row = fromRow - 1; row >= 0; row--) {
    for (let col = grid[row].length - spanCols; col >= 0; col--) {
      if (checkAreaFree(grid, row, col, spanRows, spanCols)) {
        return { row, col };
      }
    }
  }
  return undefined;
};

/**
 * Checks if a rectangular area in the grid is completely free for widget placement.
 * Verifies that all cells in the specified area are either null or undefined.
 *
 * @param grid - 2D array representing the current grid state
 * @param row - Starting row of the area to check
 * @param col - Starting column of the area to check
 * @param spanRows - Height of the area in rows
 * @param spanCols - Width of the area in columns
 * @returns True if the entire area is free, false if any cell is occupied
 */
const checkAreaFree = (
  grid: (number | null)[][],
  row: number,
  col: number,
  spanRows: number,
  spanCols: number
): boolean => {
  // Check if the area is free
  for (let r = row; r < row + spanRows; r++) {
    for (let c = col; c < col + spanCols; c++) {
      if (grid[r] && grid[r][c] !== null) {
        return false;
      }
    }
  }
  return true;
};

/**
 * Marks a rectangular area in the grid as occupied by a specific widget.
 * Sets the widget's index in all cells within the specified area.
 * Ensures grid rows exist before marking cells.
 *
 * @param grid - 2D array representing the current grid state
 * @param row - Starting row of the area to mark
 * @param col - Starting column of the area to mark
 * @param spanRows - Height of the area in rows
 * @param spanCols - Width of the area in columns
 * @param index - Widget index to store in the occupied cells
 */
const markAreaOccupied = (
  grid: (number | null)[][],
  row: number,
  col: number,
  spanRows: number,
  spanCols: number,
  index: number
) => {
  for (let r = row; r < row + spanRows; r++) {
    for (let c = col; c < col + spanCols; c++) {
      if (!grid[r]) grid[r] = new Array(grid[0].length).fill(null);
      grid[r][c] = index;
    }
  }
};
